import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Logo } from '@barev/ui';
import { match, useLocation, useToast, useTRPC } from '@barev/web';
import { useEffect, useState, type ReactNode } from 'react';
import { BO_LOCALE_LABELS, BO_LOCALES, errText, useT, type BoKey } from './i18n.tsx';
import { Avatar, I, OLD_ADMIN_URL, type IconName } from './components/kit.tsx';
import { Login } from './pages/Login.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Clients } from './pages/Clients.tsx';
import { ClientCard } from './pages/ClientCard.tsx';
import { Team } from './pages/Team.tsx';
import { Soon } from './pages/Soon.tsx';
import { Chats } from './pages/Chats.tsx';
import { Orders } from './pages/Orders.tsx';
import { OrderCard } from './pages/OrderCard.tsx';
import { Providers } from './pages/Providers.tsx';
import { ProviderCard } from './pages/ProviderCard.tsx';
import { Crm } from './pages/Crm.tsx';
import { LeadCard } from './pages/LeadCard.tsx';
import { AgencyCard, Agencies } from './pages/Agencies.tsx';
import { VenueCard, Venues } from './pages/Venues.tsx';
import { ServicePartnerCard, ServicePartners } from './pages/ServicePartners.tsx';
import { Points } from './pages/Points.tsx';
import { Products, ServiceEditor } from './pages/Products.tsx';
import { Broadcasts, Journal, Settings } from './pages/Admin5.tsx';
import { MoveCard, Moves, Requests } from './pages/Moves.tsx';

export type Role = 'SUPERADMIN' | 'SUPPORT' | 'CONCIERGE' | 'SALES';
export interface Me {
  id: string;
  name: string;
  roles: Role[];
  telegramLinked: boolean;
}

export function App() {
  const trpc = useTRPC();
  const me = useQuery({ ...trpc.bo.auth.me.queryOptions(), retry: false });
  if (me.isPending) return null;
  if (!me.data || !me.data.verified) return <Login />;
  return <Shell me={{ ...me.data, roles: me.data.roles as Role[] }} />;
}

interface NavItem {
  path: string;
  key: BoKey;
  icon: IconName;
  /** этап, на котором раздел появится; пусто — уже работает */
  stage?: number;
  /** кому виден; суперадмину — всё */
  roles: Role[] | 'all';
  group?: 'work' | 'partners' | 'setup';
  count?: 'chats' | 'orders';
}

const NAV: NavItem[] = [
  { path: '/', key: 'nav.dashboard', icon: 'dashboard', roles: 'all' },
  { path: '/clients', key: 'nav.clients', icon: 'clients', roles: ['SUPPORT', 'CONCIERGE'] },
  { path: '/chats', key: 'nav.chats', icon: 'chats', roles: ['SUPPORT', 'CONCIERGE'], count: 'chats' },
  { path: '/orders', key: 'nav.orders', icon: 'orders', roles: ['SUPPORT', 'CONCIERGE'], count: 'orders' },
  { path: '/providers', key: 'nav.providers', icon: 'providers', roles: ['SUPPORT'], group: 'work' },
  { path: '/points', key: 'nav.points', icon: 'gift', roles: [], group: 'work' },
  { path: '/agencies', key: 'nav.agencies', icon: 'agencies', roles: [], group: 'partners' },
  { path: '/service-partners', key: 'nav.servicePartners', icon: 'providers', roles: [], group: 'partners' },
  { path: '/venues', key: 'nav.venues', icon: 'venues', roles: [], group: 'partners' },
  { path: '/crm', key: 'nav.crm', icon: 'crm', roles: ['SALES'], group: 'partners' },
  { path: '/products', key: 'nav.products', icon: 'products', roles: ['SUPPORT'], group: 'setup' },
  { path: '/journal', key: 'nav.journal', icon: 'journal', roles: [], group: 'setup' },
  { path: '/team', key: 'nav.team', icon: 'team', roles: [], group: 'setup' },
  { path: '/settings', key: 'nav.settings', icon: 'gear', roles: [], group: 'setup' },
];
const GROUP_KEY = { work: 'nav.groupWork', partners: 'nav.groupPartners', setup: 'nav.groupSetup' } as const;

function Shell({ me }: { me: Me }) {
  const { t } = useT();
  const trpc = useTRPC();
  const { path } = useLocation();
  const isSuper = me.roles.includes('SUPERADMIN');
  const items = NAV.filter((n) => isSuper || n.roles === 'all' || n.roles.some((r) => me.roles.includes(r)));
  const attention = useQuery({ ...trpc.bo.dashboard.attention.queryOptions(), refetchInterval: 30_000 });
  const counts = { chats: attention.data?.unreadChats, orders: attention.data?.unassigned };
  const [sheet, setSheet] = useState(false);
  useEffect(() => {
    setSheet(false);
    window.scrollTo(0, 0);
  }, [path]);

  const current = items.find((n) => n.path === path || (n.path !== '/' && path.startsWith(`${n.path}/`)));
  let page: ReactNode;
  const clientId = match('/clients/:id', path)?.id;
  const threadId = match('/chats/:id', path)?.id;
  const canChat = items.some((n) => n.path === '/chats');
  const orderId = match('/orders/:id', path)?.id;
  const providerId = match('/providers/:id', path)?.id;
  // заказы и исполнители — поддержка; переезды и заявки — ещё и консьерж
  const canOrders = isSuper || me.roles.includes('SUPPORT');
  const canMoves = items.some((n) => n.path === '/orders');
  const moveId = match('/moves/:id', path)?.id;
  const leadId = match('/crm/:id', path)?.id;
  const agencyId = match('/agencies/:id', path)?.id;
  const venueId = match('/venues/:id', path)?.id;
  const servicePartnerId = match('/service-partners/:id', path)?.id;
  const canCrm = items.some((n) => n.path === '/crm');
  const productId = match('/products/:id', path)?.id;
  const canProducts = items.some((n) => n.path === '/products');
  const canBroadcast = isSuper || me.roles.includes('SUPPORT');
  if (path === '/') page = <Dashboard me={me} />;
  else if ((path === '/chats' || threadId) && canChat) page = <Chats me={me} threadId={threadId} />;
  else if (path === '/orders' && canOrders) page = <Orders me={me} />;
  else if ((path === '/orders' || path === '/moves') && canMoves) page = <Moves me={me} />;
  else if (moveId && canMoves) page = <MoveCard id={moveId} me={me} />;
  else if (path === '/requests' && canMoves) page = <Requests me={me} />;
  else if (orderId && canOrders) page = <OrderCard id={orderId} me={me} />;
  else if (path === '/providers' && canOrders) page = <Providers me={me} />;
  else if (providerId && canOrders) page = <ProviderCard id={providerId} me={me} />;
  else if (path === '/crm' && canCrm) page = <Crm me={me} />;
  else if (leadId && canCrm) page = <LeadCard id={leadId} me={me} />;
  else if (path === '/agencies' && isSuper) page = <Agencies />;
  else if (agencyId && isSuper) page = <AgencyCard id={agencyId} />;
  else if (path === '/points' && isSuper) page = <Points />;
  else if (path === '/service-partners' && isSuper) page = <ServicePartners />;
  else if (servicePartnerId && isSuper) page = <ServicePartnerCard id={servicePartnerId} />;
  else if (path === '/venues' && isSuper) page = <Venues />;
  else if (venueId && isSuper) page = <VenueCard id={venueId} />;
  else if (path === '/products' && canProducts) page = <Products me={me} />;
  else if (productId && canProducts) page = <ServiceEditor id={productId} me={me} />;
  else if (path === '/broadcasts' && canBroadcast) page = <Broadcasts />;
  else if (path === '/journal' && isSuper) page = <Journal />;
  else if (path === '/settings' && isSuper) page = <Settings />;
  else if (path === '/clients' && current) page = <Clients me={me} />;
  else if (clientId && items.some((n) => n.path === '/clients')) page = <ClientCard id={clientId} me={me} />;
  else if (path === '/team' && isSuper) page = <Team me={me} />;
  else if (current?.stage) page = <Soon title={t(current.key)} stage={current.stage} />;
  else page = <Dashboard me={me} />;

  const isActive = (n: NavItem) =>
    n.path === '/' ? path === '/' : path === n.path || path.startsWith(`${n.path}/`) || (n.path === '/orders' && (path.startsWith('/moves') || path === '/requests'));
  const tabItems = items.slice(0, 4);

  return (
    <div className={`bo-layout${path.startsWith('/chats') ? ' bo-layout--chats' : ''}${threadId ? ' bo-layout--thread' : ''}`}>
      <aside className="bo-side">
        <div className="bo-side__logo">
          <Logo size={22} color="#f6e9dc" />
        </div>
        <NavList items={items} isActive={isActive} counts={counts} />
        <SideFoot me={me} />
      </aside>

      <main className="bo-main">
        {!me.telegramLinked && <LinkTelegramBanner />}
        {page}
      </main>

      <nav className="bo-tabbar" aria-label="Menu">
        {tabItems.map((n) => (
          <a key={n.path} href={`#${n.path}`} className="bo-tab" aria-current={isActive(n) ? 'page' : undefined}>
            <I name={n.icon} size={22} />
            {t(n.key)}
            {!!(n.count && counts[n.count]) && <span className="bo-nav__count">{counts[n.count!]}</span>}
          </a>
        ))}
        <button type="button" className="bo-tab" onClick={() => setSheet(true)} aria-expanded={sheet}>
          <I name="more" size={22} />
          {t('nav.more')}
        </button>
      </nav>
      {sheet && (
        <>
          <div className="bo-sheet-backdrop" onClick={() => setSheet(false)} />
          <div className="bo-sheet" role="dialog" aria-label={t('nav.more')}>
            <NavList items={items.slice(4)} isActive={isActive} counts={counts} />
            <SideFoot me={me} />
          </div>
        </>
      )}
    </div>
  );
}

function NavList({ items, isActive, counts }: { items: NavItem[]; isActive: (n: NavItem) => boolean; counts: Record<'chats' | 'orders', number | undefined> }) {
  const { t } = useT();
  let lastGroup: string | undefined;
  return (
    <>
      {items.map((n) => {
        const header = n.group && n.group !== lastGroup ? <div className="bo-side__group">{t(GROUP_KEY[n.group])}</div> : null;
        lastGroup = n.group ?? lastGroup;
        const count = n.count ? counts[n.count] : undefined;
        return (
          <div key={n.path}>
            {header}
            <a href={`#${n.path}`} className="bo-nav" aria-current={isActive(n) ? 'page' : undefined}>
              <I name={n.icon} />
              {t(n.key)}
              {count ? <span className="bo-nav__count">{count}</span> : n.stage ? <span className="bo-nav__soon">{t('nav.soon', { n: n.stage })}</span> : null}
            </a>
          </div>
        );
      })}
    </>
  );
}

function SideFoot({ me }: { me: Me }) {
  const { t, locale, setLocale } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const logout = useMutation(trpc.bo.auth.logout.mutationOptions({ onSuccess: () => qc.resetQueries() }));
  return (
    <div className="bo-side__foot">
      <div className="bo-langs" role="group" aria-label="Language">
        {BO_LOCALES.map((l) => (
          <button key={l} type="button" className="bo-lang" aria-pressed={l === locale} onClick={() => setLocale(l)}>
            {BO_LOCALE_LABELS[l]}
          </button>
        ))}
      </div>
      <div className="bo-me">
        <Avatar name={me.name} id={me.id} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{me.name}</div>
          <div className="bo-me__roles">{me.roles.map((r) => t(`role.${r}`)).join(', ')}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <a className="bo-side__link" href={OLD_ADMIN_URL} target="_blank" rel="noreferrer">
          {t('shell.oldAdmin')}
        </a>
        <button type="button" className="bo-side__link" onClick={() => logout.mutate()}>
          {t('shell.logout')}
        </button>
      </div>
    </div>
  );
}

function LinkTelegramBanner() {
  const { t } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const link = useMutation(
    trpc.bo.auth.telegramLink.mutationOptions({
      onSuccess: (r) => window.open(r.url, '_blank', 'noopener'),
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <div className="bo-banner">
      <span>{t('shell.linkTg')}</span>
      <button type="button" className="bo-btn bo-btn--sm" onClick={() => link.mutate()} disabled={link.isPending}>
        <I name="telegram" size={16} />
        {t('shell.linkTgBtn')}
      </button>
    </div>
  );
}
